import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useState } from "react";
import {
  LineChart,
  Eye,
  Activity,
  Search,
  ClipboardEdit,
  History,
  Sparkles,
  BookText,
  LogOut,
  Square,
  Shield,
  KeyRound,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import ChangePasswordDialog from "./ChangePasswordDialog";

const NAV = [
  { to: "/stocks-in-play", label: "Stocks in Play", icon: LineChart, testid: "nav-stocks-in-play" },
  { to: "/watchlist", label: "Watchlist", icon: Eye, testid: "nav-watchlist" },
  { to: "/market-conditions", label: "Market Conditions", icon: Activity, testid: "nav-market-conditions" },
  { to: "/check-stock", label: "Check Stock", icon: Search, testid: "nav-check-stock" },
  { to: "/market-details", label: "Enter Market Details", icon: ClipboardEdit, testid: "nav-market-details" },
  { to: "/history", label: "History", icon: History, testid: "nav-history" },
  { to: "/manual", label: "Manual", icon: BookText, testid: "nav-manual" },
  { to: "/our-offer", label: "Our Offer", icon: Sparkles, testid: "nav-our-offer", hideForMember: true },
];

const ADMIN_NAV = { to: "/admin", label: "Admin", icon: Shield, testid: "nav-admin" };

function PageTitle() {
  const location = useLocation();
  const allNav = [...NAV, ADMIN_NAV];
  const item = allNav.find((n) => n.to === location.pathname);
  return (
    <div>
      <div className="text-xs font-medium tracking-[0.2em] text-slate-500 uppercase">Dashboard</div>
      <h1 className="text-2xl font-bold tracking-tight text-slate-900" data-testid="page-title">
        {item?.label || "Overview"}
      </h1>
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const [showPwd, setShowPwd] = useState(false);
  return (
    <div className="min-h-screen bg-white text-slate-900 font-body">
      <div className="flex">
        {/* Sidebar */}
        <aside
          className="hidden md:flex w-64 shrink-0 flex-col border-r border-slate-200 min-h-screen sticky top-0"
          data-testid="sidebar"
        >
          <div className="px-6 py-6 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-slate-900 flex items-center justify-center rounded-sm">
                <Square className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
              </div>
              <div className="font-heading font-black tracking-tighter text-lg text-slate-900">
                BAYESIAN
              </div>
            </div>
            <div className="text-xs font-medium tracking-[0.2em] text-slate-500 uppercase mt-1">
              Options
            </div>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-1">
            {NAV.filter((n) => !(n.hideForMember && user?.role === "user")).map(({ to, label, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                    isActive
                      ? "bg-slate-100 text-slate-900 font-semibold"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={1.75} />
                <span>{label}</span>
              </NavLink>
            ))}
            {user?.role === "admin" && (
              <>
                <div className="my-3 border-t border-slate-200" />
                <NavLink
                  to={ADMIN_NAV.to}
                  data-testid={ADMIN_NAV.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                      isActive
                        ? "bg-slate-100 text-slate-900 font-semibold"
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`
                  }
                >
                  <ADMIN_NAV.icon className="w-4 h-4" strokeWidth={1.75} />
                  <span>{ADMIN_NAV.label}</span>
                </NavLink>
              </>
            )}
          </nav>

          <div className="px-4 py-4 border-t border-slate-200">
            <div className="text-xs text-slate-500 mb-1 truncate" data-testid="sidebar-user-email">
              {user?.email}
            </div>
            {user?.role === "guest" && (
              <div
                className="inline-flex items-center gap-1 mb-2 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-semibold tracking-[0.15em] uppercase"
                data-testid="guest-badge"
              >
                Guest
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPwd(true)}
              data-testid="change-password-button"
              className="w-full justify-start gap-2 mb-2"
            >
              <KeyRound className="w-4 h-4" />
              Change password
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              data-testid="logout-button"
              className="w-full justify-start gap-2"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 min-w-0">
          <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-xl border-b border-slate-200">
            <div className="px-6 sm:px-8 lg:px-10 py-5 flex items-center justify-between">
              <PageTitle />
              <div className="hidden sm:flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-slate-500">
                <span>{user?.name || user?.email}</span>
              </div>
            </div>
          </header>

          <main className="px-6 sm:px-8 lg:px-10 py-8">
            <Outlet />
          </main>
        </div>
      </div>
      <ChangePasswordDialog open={showPwd} onOpenChange={setShowPwd} />
    </div>
  );
}
